import "react-tooltip/dist/react-tooltip.css";
import "./CustomTooltip.css";

import {
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import AddIcon from "../../resources/addIcon";
import EyeIcon from "../../resources/eyeIcon";
import FileIcon from "../../resources/fileIcon";
import Loader from "./Loader/Loader";
import ReactDOM from "react-dom";
import { SVTUserContext } from "../../utils/SVTUserContext";
import { Tooltip } from "react-tooltip";
import { deleteEvidence } from "../../services/svt/svt-user";
import useGetSasTokenForEvidence from "../../utils/useGetSasTokenForEvidence";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getSafeHtmlFromDb } from "../../utils/sanitizeSvgFile";
import validateFileFormat from "../../utils/ValidationFileFormat";

const AssessmentTable = forwardRef(({ data }, ref) => {
    const { t } = useTranslation();
    const [tooltipVisible, setTooltipVisible] = useState({});
    const [isClient, setIsClient] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const dotRefs = useRef({});
    const evidenceList = (data || []).map((file) => file.evidenceFile);
    const [uploadedFiles, setUploadedFiles] = useState(evidenceList);
    const yesNoRefs = useRef([]);
    const commentsRefs = useRef([]);
    const { isReview, isSave, assessmentResponses, activeLevelId } =
        useContext(SVTUserContext);
    const [sasToken, loading] = useGetSasTokenForEvidence();
    const responseIndex =
        assessmentResponses?.length > 0
            ? assessmentResponses.findIndex(
                  (item) => item.levelId === activeLevelId
              )
            : -1;

    useImperativeHandle(ref, () => ({
        getTableData,
    }));

    useEffect(() => {
        setIsClient(true); //Used to check if the component is rendered on the client side
    }, []);

    const showTooltip = (index) => {
        setTooltipVisible({ [index]: true });
    };

    const hideTooltip = (index) => {
        setTooltipVisible((prev) => ({ ...prev, [index]: false }));
    };

    const handleFileUpload = (event, index) => {
        const file = event.target.files[0];
        if (!file) return;

        // const validFileTypes = [
        //     "application/pdf",
        //     "application/msword",
        //     "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        // ];


        if (!validateFileFormat(file)) {
          return;
        }

        if (file.size <= 1048576) {
            setUploadedFiles((prevFiles) => ({
                ...prevFiles,
                [index]: file,
            }));
        } else {
            toast.error(t("fileUploadError"));
        }
    };

    const handleFileRemove = (index, item) => {
        setUploadedFiles((prevFiles) => {
            const newFiles = { ...prevFiles };
            delete newFiles[index];
            return newFiles;
        });
        if (
            typeof uploadedFiles[index] === "string" &&
            assessmentResponses?.length > 0
        ) {
            const assessmentSubmissionId = assessmentResponses.filter(
                (ar) => ar.levelId === activeLevelId
            )[0]?.id;
            handleDeleteEvidence(assessmentSubmissionId, item.id);
        }
    };

    const handleDeleteEvidence = async (assessmentSubmissionId, questionId) => {
        try {
            const res = await deleteEvidence(
                assessmentSubmissionId,
                questionId
            );
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const getTableData = () => {
        const tableData = (data || []).map((item, index) => {
            const yesNoValue =
                yesNoRefs.current[index]?.find((input) => input.checked)
                    ?.value || null;
            const evidence = uploadedFiles[index] || null;
            const comments = commentsRefs.current[index]?.value || "";

            return {
                questionId: item.id,
                userAnswer: yesNoValue,
                evidenceFile: evidence,
                comments: comments,
            };
        });
        return tableData;
    };

    const getTableRowClass = (item) => {
        if (item.isCorrect === undefined) return "";
        else if (item.isCorrect) return "table-row-green";
        else return "table-row-red";
    };

    const openFileInNewTab = (file) => {
        if (file) {
            if (file instanceof File) {
                const fileURL = URL.createObjectURL(file);
                window.open(fileURL, "_blank");
            } else if (typeof file === "string") {
                window.open(file + sasToken, "_blank");
            } else {
                console.error("Unsupported file type");
            }
        }
    };

    if (loading) return <Loader />;
    return (
        <div>
            <table className="assessment-table">
                <thead>
                    <tr className="assessment-table-header-row">
                        <th className="question-header">{t("question")}</th>
                        <th className="yesno-header">{t("yesNo")}</th>
                        <th className="evidence-header">{t("evidence")}</th>
                        <th className="comments-header">{t("comments")}</th>
                        <th className="guidance-header">
                            {t("hitachiGuidance")}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {(data || []).map((item, index) => (
                        <tr className={getTableRowClass(item)} key={index}>
                            <td className="table-cell">
                                {index + 1 + ". " + item.questionText}
                            </td>
                            <td className="table-cell">
                                <div className="table-cell-div">
                                    <label className="table-yesno-label">
                                        <input
                                            type="radio"
                                            name={`yes-no-${index}`}
                                            value="1"
                                            disabled={isReview}
                                            defaultChecked={
                                                item.userAnswer === "1"
                                            }
                                            ref={(el) => {
                                                if (!yesNoRefs.current[index])
                                                    yesNoRefs.current[index] =
                                                        [];
                                                yesNoRefs.current[index][0] =
                                                    el;
                                            }}
                                        />
                                        {isReview ||
                                        isSave ||
                                        responseIndex !== -1
                                            ? t(item["option#1"])
                                            : t(item?.options[1])}
                                    </label>
                                    <label className="table-yesno-label">
                                        <input
                                            type="radio"
                                            name={`yes-no-${index}`}
                                            value="2"
                                            disabled={isReview}
                                            defaultChecked={
                                                item.userAnswer === "2"
                                            }
                                            ref={(el) => {
                                                if (!yesNoRefs.current[index])
                                                    yesNoRefs.current[index] =
                                                        [];
                                                yesNoRefs.current[index][1] =
                                                    el;
                                            }}
                                        />
                                        {isReview ||
                                        isSave ||
                                        responseIndex !== -1
                                            ? t(item["option#2"])
                                            : t(item?.options[2])}
                                    </label>
                                </div>
                            </td>
                            <td className="table-cell">
                                <div className="table-cell-pointer">
                                    {uploadedFiles[index] ? (
                                        <div className="table-upload-div">
                                            <div
                                                className="table-upload-fileicon-wrap"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div
                                                    onClick={() =>
                                                        openFileInNewTab(
                                                            uploadedFiles[index]
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className="table-cell-pointer"
                                                        data-tooltip-id={`file-tooltip-${index}`}
                                                        data-tooltip-content={
                                                            uploadedFiles[index]
                                                                .name
                                                                ? uploadedFiles[
                                                                      index
                                                                  ].name
                                                                : decodeURIComponent(
                                                                      uploadedFiles[
                                                                          index
                                                                      ].substring(
                                                                          uploadedFiles[
                                                                              index
                                                                          ].lastIndexOf(
                                                                              "/"
                                                                          ) + 1
                                                                      )
                                                                  )
                                                        }
                                                    >
                                                        <FileIcon />
                                                    </div>
                                                    <Tooltip
                                                        id={`file-tooltip-${index}`}
                                                        style={{
                                                            zIndex: "1000",
                                                        }}
                                                    />
                                                </div>
                                                {!isReview && (
                                                    <span
                                                        className="table-upload-remove"
                                                        onClick={() =>
                                                            handleFileRemove(
                                                                index,
                                                                item
                                                            )
                                                        }
                                                        title="Remove file"
                                                    >
                                                        X
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <label data-tooltip-id={`upload-tooltip-${index}`}
                                            data-tooltip-content={t('singleUpload')}>
                                            {!isReview && <AddIcon />}
                                            <input
                                                type="file"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.jpg"
                                                className="table-upload-input"
                                                onChange={(event) =>
                                                    handleFileUpload(
                                                        event,
                                                        index
                                                    )
                                                }
                                            />
                                                <Tooltip
                                                    id={`upload-tooltip-${index}`}
                                                    place="bottom"
                                                />
                                        </label>
                                    )}
                                </div>
                            </td>
                            <td className="table-cell">
                                <div
                                    className="table-cell-div"
                                    style={{ height: "2rem" }}
                                    data-tooltip-id={`comments-tooltip-${index}`}
                                    data-tooltip-content={item.comments || ""}
                                >
                                    <input
                                        type="text"
                                        disabled={isReview}
                                        defaultValue={item.comments || ""}
                                        ref={(el) =>
                                            (commentsRefs.current[index] = el)
                                        }
                                    />
                                    <Tooltip
                                        id={`comments-tooltip-${index}`}
                                        place="bottom"
                                        className="custom-tooltip"
                                    />
                                </div>
                            </td>
                            <td
                                className="table-cell"
                                style={{ position: "relative" }}
                            >
                                <div
                                    className="table-cell-pointer"
                                    ref={(el) => (dotRefs.current[index] = el)}
                                    onMouseEnter={() => {
                                        showTooltip(index);
                                        const el = dotRefs.current[index];
                                        if (el) {
                                            const rect =
                                                el.getBoundingClientRect();
                                            setCoords({
                                                top: rect.top,
                                                left: rect.left,
                                            });
                                        }
                                    }}
                                >
                                    <EyeIcon />
                                </div>

                                {isClient &&
                                    tooltipVisible[index] &&
                                    ReactDOM.createPortal(
                                        <>
                                            {item.hitachiGuidance && (
                                                <div
                                                    style={{
                                                        top: coords.top,
                                                        left: coords.left - 600,
                                                    }}
                                                    className="tooltip-content"
                                                    onMouseLeave={() =>
                                                        hideTooltip(index)
                                                    }
                                                >
                                                    <div>
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: getSafeHtmlFromDb(item.hitachiGuidance),
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>,
                                        document.body // <-- This is required!
                                    )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
});

export default AssessmentTable;
